import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import {
  Trophy,
  Skull,
  Sparkles,
  XCircle,
  Check,
  Phone,
  TrendingUp,
  CirclePlus,
  Bot,
  Swords,
  Copy,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Table } from 'poker-ts';
import { AudioProvider, useAudio } from '../poker-ui/AudioProvider';
import { cn } from '../poker-ui/utils/cn';
import { GameIndustrialHeader } from '../poker-ui/GameIndustrialHeader';
import { ArcadeModeBackdrop } from '../poker-ui/ArcadeModeBackdrop';
import { PokerTableSurface } from '../poker-ui/PokerTableSurface';
import { IndustrialSeat } from '../poker-ui/IndustrialSeat';
import { PlayerHandStrip } from '../poker-ui/PlayerHandStrip';
import { PlayerStackBar } from '../poker-ui/PlayerStackBar';
import { SekaActionBar } from '../poker-ui/SekaActionBar';
import { PokerBetRaiseSlider } from '../poker-ui/PokerBetRaiseSlider';
import {
  buildEllipticSeatSlots,
  getSeatDensityPreset,
  getSeatArenaTuning,
} from '../poker-ui/seatLayout';
import {
  evaluateHand,
  boardToCommunitySlots,
  pokerTsCardToEngineCard,
  pokerTsCardToPlayingCard,
} from '../game/pokerEngine';
import { PlayingCard } from '../poker-ui/PlayingCard';
import { MatchChronicleChain } from '../poker-ui/MatchChronicleChain';

const N = 4;
const BOT_COUNT_OPTIONS = [1, 2, 3];
const SB = 5;
const BB = 10;
const START_STACK = 1000;
/** Лимит времени на ход героя (как в онлайн-румах). */
const HERO_TURN_SECONDS = 30;
/** Задержка «размышления» бота (мс); кольцо хода на сиденье привязано к тому же окну. */
const BOT_TURN_DELAY_MS = 380;
const BOT_TURN_DELAY_HERO_FOLDED_MS = 120;

/** Точки «фейерверка» на экране полной победы (%, задержка, цвет). */
const GRAND_VICTORY_SPARKS = [
  { l: 6, t: 16, d: 0, c: '#fbbf24' },
  { l: 16, t: 8, d: 0.12, c: '#f472b6' },
  { l: 88, t: 20, d: 0.28, c: '#22d3ee' },
  { l: 78, t: 10, d: 0.04, c: '#a78bfa' },
  { l: 10, t: 52, d: 0.38, c: '#fde047' },
  { l: 42, t: 6, d: 0.22, c: '#fb7185' },
  { l: 52, t: 68, d: 0.52, c: '#34d399' },
  { l: 92, t: 46, d: 0.18, c: '#fbbf24' },
  { l: 26, t: 36, d: 0.62, c: '#60a5fa' },
  { l: 70, t: 38, d: 0.32, c: '#f472b6' },
  { l: 36, t: 58, d: 0.48, c: '#fde047' },
  { l: 94, t: 14, d: 0.42, c: '#22d3ee' },
  { l: 48, t: 22, d: 0.08, c: '#fb923c' },
  { l: 22, t: 72, d: 0.58, c: '#a78bfa' },
  { l: 62, t: 14, d: 0.26, c: '#4ade80' },
];

/** Подпись улицы для подсказки «последний ход». */
function streetLabelRu(rob) {
  switch (rob) {
    case 'preflop':
      return 'Префлоп';
    case 'flop':
      return 'Флоп';
    case 'turn':
      return 'Тёрн';
    case 'river':
      return 'Ривер';
    default:
      return rob || '';
  }
}

/** Бейдж последнего действия игрока (фокус/hover для доступности). */
function SeatLastMoveBadge({ action, isDimmed }) {
  if (!action?.kind) return null;

  const { kind, amount, street } = action;
  const streetStr = street ? streetLabelRu(street) : '';
  const amtStr =
    amount != null && Number.isFinite(Number(amount))
      ? Number(amount).toLocaleString('ru-RU')
      : null;

  let Icon = Check;
  let short = '';
  let full = '';
  /** Лёгкий «окрас» бейджа по типу хода (рамка / фон / текст / иконка). */
  let tone = {
    shell:
      'border-border/80 bg-muted/25 text-foreground/90 hover:border-neon-cyan/40 hover:bg-muted/45 hover:shadow-[0_0_12px_rgba(0,255,255,0.12)]',
    ringFocus: 'focus-visible:ring-neon-cyan/50',
    icon: 'text-muted-foreground',
  };
  switch (kind) {
    case 'fold':
      Icon = XCircle;
      short = 'Пас';
      full = streetStr ? `${streetStr}: пас` : 'Пас';
      tone = {
        shell:
          'border-red-500/35 bg-red-950/25 text-red-100/95 hover:border-red-400/50 hover:bg-red-950/40 hover:shadow-[0_0_14px_rgba(248,113,113,0.18)]',
        ringFocus: 'focus-visible:ring-red-400/45',
        icon: 'text-red-400/95',
      };
      break;
    case 'check':
      Icon = Check;
      short = 'Чек';
      full = streetStr ? `${streetStr}: чек` : 'Чек';
      tone = {
        shell:
          'border-emerald-500/30 bg-emerald-950/20 text-emerald-50/95 hover:border-emerald-400/45 hover:bg-emerald-950/35 hover:shadow-[0_0_14px_rgba(52,211,153,0.14)]',
        ringFocus: 'focus-visible:ring-emerald-400/45',
        icon: 'text-emerald-400/95',
      };
      break;
    case 'call':
      Icon = Phone;
      short = 'Колл';
      full = streetStr ? `${streetStr}: колл` : 'Колл';
      tone = {
        shell:
          'border-cyan-500/35 bg-cyan-950/20 text-cyan-50/95 hover:border-neon-cyan/50 hover:bg-cyan-950/38 hover:shadow-[0_0_14px_rgba(34,211,238,0.16)]',
        ringFocus: 'focus-visible:ring-neon-cyan/50',
        icon: 'text-neon-cyan',
      };
      break;
    case 'bet':
      Icon = CirclePlus;
      short = amtStr ? `Бет ${amtStr}` : 'Бет';
      full = streetStr ? `${streetStr}: бет${amtStr ? ` ${amtStr}` : ''}` : `Бет${amtStr ? ` ${amtStr}` : ''}`;
      tone = {
        shell:
          'border-amber-400/35 bg-amber-950/25 text-amber-50/95 hover:border-amber-300/50 hover:bg-amber-950/42 hover:shadow-[0_0_14px_rgba(251,191,36,0.16)]',
        ringFocus: 'focus-visible:ring-amber-400/45',
        icon: 'text-amber-300/95',
      };
      break;
    case 'raise':
      Icon = TrendingUp;
      short = amtStr ? `Рейз ${amtStr}` : 'Рейз';
      full = streetStr ? `${streetStr}: рейз${amtStr ? ` до ${amtStr}` : ''}` : `Рейз${amtStr ? ` до ${amtStr}` : ''}`;
      tone = {
        shell:
          'border-neon-orange/40 bg-orange-950/25 text-orange-50/95 hover:border-neon-orange/60 hover:bg-orange-950/40 hover:shadow-[0_0_14px_rgba(251,146,60,0.2)]',
        ringFocus: 'focus-visible:ring-neon-orange/50',
        icon: 'text-neon-orange',
      };
      break;
    default:
      short = kind;
      full = kind;
  }

  return (
    <div
      tabIndex={0}
      role="note"
      aria-label={`Последний ход: ${full}`}
      title={full}
      className={cn(
        'mt-1 flex max-w-[132px] cursor-default select-none items-center justify-center gap-1 rounded-md border px-2 py-0.5',
        'text-[10px] font-semibold leading-tight shadow-sm outline-none transition',
        tone.shell,
        'focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-offset-background',
        tone.ringFocus,
        'active:scale-[0.97]',
        isDimmed && 'opacity-45'
      )}
    >
      <Icon className={cn('h-3 w-3 shrink-0 opacity-95', tone.icon)} aria-hidden />
      <span className="truncate">{short}</span>
    </div>
  );
}

const HAND_RANKING_RU = {
  0: 'Старшая карта',
  1: 'Пара',
  2: 'Две пары',
  3: 'Тройка',
  4: 'Стрит',
  5: 'Флеш',
  6: 'Фул-хаус',
  7: 'Каре',
  8: 'Стрит-флеш',
  9: 'Роял-флеш',
};

function createTable(totalPlayers = N) {
  const table = new Table({ smallBlind: SB, bigBlind: BB }, N);
  const seated = Math.max(2, Math.min(N, Number(totalPlayers) || 2));
  for (let i = 0; i < seated; i += 1) {
    table.sitDown(i, START_STACK);
  }
  return table;
}

function countSeatedPlayers(table) {
  return table.seats().filter((s) => s !== null).length;
}

/** Соперники вылетели; poker-ts требует ≥2 для startHand — без этого будет assert. */
function isHeroSoloAtTable(table) {
  const seats = table.seats();
  return seats[0] != null && seats.slice(1).every((s) => s === null);
}

function biggestBet(handPlayers) {
  let m = 0;
  for (const p of handPlayers) {
    if (p && p.betSize > m) m = p.betSize;
  }
  return m;
}

function callAmount(table) {
  const hp = table.handPlayers();
  const h = hp[0];
  if (!h) return 0;
  const gap = biggestBet(hp) - h.betSize;
  return Math.max(0, Math.min(gap, h.stack));
}

function pickAggressiveBetSize(chipRange) {
  if (!chipRange) return undefined;
  const { min, max } = chipRange;
  if (min > max) return min;
  if (min === max) return min;
  const span = max - min;
  const jitter = Math.floor(Math.random() * Math.min(span + 1, Math.max(1, BB * 3)));
  return Math.min(max, min + jitter);
}

/**
 * После `actionTaken`: доводим улицы до паузы (ждём ход) или до шоудауна.
 * @returns {object|null} метаданные конца раздачи или null
 */
function advanceUntilPauseOrDone(table) {
  while (table.isHandInProgress()) {
    if (table.isBettingRoundInProgress()) {
      return null;
    }
    if (table.areBettingRoundsCompleted()) {
      return finalizeShowdown(table);
    }
    table.endBettingRound();
  }
  return null;
}

/** Текст «кто забрал банк / поделил» для итога раздачи. */
function formatPotWinnerSummary(winnerSeats, names) {
  if (!winnerSeats?.length) return null;
  const sorted = [...winnerSeats].sort((a, b) => a - b);
  const labels = sorted.map((i) => (names[i] || `Место ${i}`).trim());
  if (labels.length === 1) return `Партию забрал: ${labels[0]}`;
  return `Банк поделили: ${labels.join(' · ')}`;
}

function formatPokerSeatActionForExport(action) {
  if (!action?.kind) return '';
  const streetStr = action.street ? streetLabelRu(action.street) : '';
  const amtStr =
    action.amount != null && Number.isFinite(Number(action.amount))
      ? Number(action.amount).toLocaleString('ru-RU')
      : null;
  switch (action.kind) {
    case 'fold':
      return streetStr ? `${streetStr}: пас` : 'Пас';
    case 'check':
      return streetStr ? `${streetStr}: чек` : 'Чек';
    case 'call':
      return streetStr ? `${streetStr}: колл` : 'Колл';
    case 'bet':
      return streetStr
        ? `${streetStr}: бет${amtStr ? ` ${amtStr}` : ''}`
        : `Бет${amtStr ? ` ${amtStr}` : ''}`;
    case 'raise':
      return streetStr
        ? `${streetStr}: рейз${amtStr ? ` до ${amtStr}` : ''}`
        : `Рейз${amtStr ? ` до ${amtStr}` : ''}`;
    default:
      return String(action.kind);
  }
}

function buildPokerPracticeOutcomeExport({
  handNo,
  names,
  handOutcomeStackDelta,
  winnerSummary,
  handOver,
  lastSeatActions,
  seatedSeats,
}) {
  const lines = [];
  lines.push('=== Покер — практика — снимок раздачи ===');
  lines.push(`Время: ${new Date().toLocaleString('ru-RU')}`);
  lines.push(`Раздача #${handNo}`);
  lines.push('');
  if (winnerSummary) lines.push(winnerSummary);
  if (handOutcomeStackDelta != null) {
    lines.push(
      `Изменение стека героя: ${handOutcomeStackDelta > 0 ? '+' : ''}${handOutcomeStackDelta.toLocaleString('ru-RU')} SEKA`
    );
  }
  lines.push('');
  if (handOver?.boardSnap?.length) {
    lines.push(`Борд: ${handOver.boardSnap.map((c) => `${c.rank}${c.suit}`).join(' ')}`);
  }
  lines.push('Последние действия по местам:');
  let anySeat = false;
  seatedSeats.forEach((seat) => {
    const a = lastSeatActions[seat];
    if (!a?.kind) return;
    anySeat = true;
    const n = names[seat] || `Место ${seat}`;
    lines.push(`  ${n}: ${formatPokerSeatActionForExport(a)}`);
  });
  if (!anySeat) lines.push('  (нет меток)');
  if (handOver?.lastEval?.length) {
    lines.push('Вскрытие:');
    handOver.lastEval.forEach((row) => {
      const n = names[row.seat] ?? row.seat;
      lines.push(`  ${n}: ${row.name}`);
    });
  }
  return lines.join('\n');
}

function collectWinnerSeatSet(table, potsBeforeShowdown) {
  const set = new Set();
  if (
    potsBeforeShowdown.length === 1 &&
    potsBeforeShowdown[0].eligiblePlayers.length === 1
  ) {
    set.add(potsBeforeShowdown[0].eligiblePlayers[0]);
    return set;
  }
  const raw = table.winners();
  for (const pot of raw) {
    for (const row of pot) {
      set.add(row[0]);
    }
  }
  return set;
}

function buildLastEval(table) {
  const raw = table.winners();
  const rows = [];
  for (const pot of raw) {
    for (const row of pot) {
      const [seat, info] = row;
      rows.push({
        seat,
        name: HAND_RANKING_RU[info.ranking] ?? `Комбинация ${info.ranking}`,
      });
    }
  }
  return rows;
}

function finalizeShowdown(table) {
  const button = table.button();
  const potsBefore = table.pots().map((p) => ({
    size: p.size,
    eligiblePlayers: Array.isArray(p.eligiblePlayers) ? [...p.eligiblePlayers] : [...p.eligiblePlayers()],
  }));

  const foldedSeats = [...Array(N).keys()].map(
    (i) => table.handPlayers()[i] === null && table.holeCards()[i] != null
  );

  const holesSnap = table.holeCards().map((h) => (h ? h.map((c) => ({ rank: c.rank, suit: c.suit })) : null));
  const boardSnap = table.communityCards().map((c) => ({ rank: c.rank, suit: c.suit }));

  table.showdown();

  const winnerSeats = collectWinnerSeatSet(table, potsBefore);
  const contested =
    potsBefore.length > 1 || (potsBefore[0] && potsBefore[0].eligiblePlayers.length > 1);
  const lastEval = contested ? buildLastEval(table) : [];

  return {
    button,
    holesSnap,
    boardSnap,
    foldedSeats,
    winnerSeats: [...winnerSeats],
    lastEval,
  };
}

/**
 * Ход бота; возвращает метку для UI «последний ход» или null.
 * @returns {{ seat: number, street: string, kind: string, amount?: number } | null}
 */
function botPlay(table, play) {
  if (!table.isHandInProgress() || !table.isBettingRoundInProgress()) return null;
  const seat = table.playerToAct();
  if (seat === 0) return null;

  const street = table.roundOfBetting();
  const { actions, chipRange } = table.legalActions();
  const canFold = actions.includes('fold');
  const canCheck = actions.includes('check');
  const canCall = actions.includes('call');
  const canBet = actions.includes('bet');
  const canRaise = actions.includes('raise');

  const r = Math.random();

  if (canFold && r < 0.11) {
    play('fold');
    table.actionTaken('fold');
    return { seat, street, kind: 'fold' };
  }
  if (canCheck && r < 0.62) {
    play('check');
    table.actionTaken('check');
    return { seat, street, kind: 'check' };
  }
  if (canCall && r < 0.82) {
    play('call');
    table.actionTaken('call');
    return { seat, street, kind: 'call' };
  }
  if (canRaise && chipRange) {
    const amt = pickAggressiveBetSize(chipRange);
    play('raise');
    table.actionTaken('raise', amt);
    return { seat, street, kind: 'raise', amount: amt };
  }
  if (canBet && chipRange) {
    const amt = pickAggressiveBetSize(chipRange);
    play('raise');
    table.actionTaken('bet', amt);
    return { seat, street, kind: 'bet', amount: amt };
  }
  if (canCheck) {
    play('check');
    table.actionTaken('check');
    return { seat, street, kind: 'check' };
  }
  if (canCall) {
    play('call');
    table.actionTaken('call');
    return { seat, street, kind: 'call' };
  }
  if (canFold) {
    play('fold');
    table.actionTaken('fold');
    return { seat, street, kind: 'fold' };
  }
  return null;
}

function PokerHuPracticeInner({ user, onBack }) {
  const { play } = useAudio();
  const [botCount, setBotCount] = useState(1);
  const [gameStarted, setGameStarted] = useState(false);
  const tableRef = useRef(null);
  if (!tableRef.current) {
    tableRef.current = createTable(2);
  }

  const [uiTick, setUiTick] = useState(0);
  const [handNo, setHandNo] = useState(0);
  const [handOver, setHandOver] = useState(null);
  const [heroStackStart, setHeroStackStart] = useState(() => {
    const t = tableRef.current;
    return t.seats()[0]?.totalChips ?? START_STACK;
  });
  const [balanceFx, setBalanceFx] = useState(null);
  const [floatingDelta, setFloatingDelta] = useState(null);
  /** Последний зафиксированный ход по местам (для бейджей у сидений). */
  const [lastSeatActions, setLastSeatActions] = useState(() => [null, null, null, null]);
  /** Выбранный размер бет/рейза (фишки); синхронизируется с `raiseRange`). */
  const [raiseAmount, setRaiseAmount] = useState(null);
  /** Оставшиеся секунды на ход героя; null — не ваш ход или пауза. */
  const [heroTurnSecondsLeft, setHeroTurnSecondsLeft] = useState(null);
  /** Дедлайн хода бота для кольца на сиденье (тот же интервал, что и setTimeout перед botPlay). */
  const [oppTurnClock, setOppTurnClock] = useState(null);
  const [, setOppRingTick] = useState(0);
  const [outcomeCopyFeedback, setOutcomeCopyFeedback] = useState(false);
  const [pokerOutcomeDetailsExpanded, setPokerOutcomeDetailsExpanded] = useState(false);
  const outcomeCopyTimerRef = useRef(null);

  const tagSeatAction = useCallback((seat, payload) => {
    if (seat == null || seat < 0 || seat >= N) return;
    setLastSeatActions((prev) => {
      const next = [...prev];
      next[seat] = payload ? { ...payload, at: Date.now() } : null;
      return next;
    });
  }, []);

  const names = useMemo(
    () => [user?.username || 'Вы', 'Бот 1', 'Бот 2', 'Бот 3'],
    [user?.username]
  );

  const seatedSeats = (() => {
    const t = tableRef.current;
    if (!t) return [0, 1];
    const seats = t.seats();
    const idx = [];
    for (let i = 0; i < seats.length; i += 1) {
      if (seats[i] != null) idx.push(i);
    }
    return idx.length >= 2 ? idx : [0, 1];
  })();

  const bump = useCallback(() => {
    setUiTick((x) => x + 1);
  }, []);

  const outcomeKind = useMemo(() => {
    if (!handOver) return null;
    const ws = new Set(handOver.winnerSeats);
    if (!ws.has(0)) return 'lose';
    if (ws.size === 1) return 'win';
    return 'chop';
  }, [handOver]);

  /** Изменение стека героя за раздачу (для итогового окна). */
  const handOutcomeStackDelta = useMemo(() => {
    if (!handOver) return null;
    const t = tableRef.current;
    if (!t) return null;
    return (t.seats()[0]?.totalChips ?? 0) - heroStackStart;
  }, [handOver, heroStackStart]);

  // uiTick: Table мутируется in-place; инкремент после каждого хода заставляет пересчитать представление.
  const view = useMemo(() => {
    const t = tableRef.current;
    if (!t) return null;

    if (handOver) {
      const seats = t.seats();
      const players = names.map((name, i) => {
        const st = seats[i];
        return {
          name,
          chips: st ? st.stack : 0,
          bet: st ? st.betSize : 0,
          folded: Boolean(handOver.foldedSeats[i]),
          hole: handOver.holesSnap[i] || [],
        };
      });
      return {
        status: 'handOver',
        dealer: handOver.button,
        acting: null,
        board: handOver.boardSnap,
        message: '',
        players,
        pot: 0,
        lastEval: handOver.lastEval,
        winnerSeats: handOver.winnerSeats,
        winnerSummary: formatPotWinnerSummary(handOver.winnerSeats, names),
      };
    }

    if (!t.isHandInProgress()) {
      return {
        status: 'idle',
        dealer: 0,
        acting: null,
        board: [],
        message: '',
        players: names.map((name, i) => ({
          name,
          chips: t.seats()[i]?.stack ?? 0,
          bet: 0,
          folded: false,
          hole: [],
        })),
        pot: 0,
        lastEval: [],
        winnerSeats: [],
        winnerSummary: null,
      };
    }

    const hp = t.handPlayers();
    const seats = t.seats();
    const holes = t.holeCards();
    const board = t.communityCards();
    const pots = t.pots();
    const mainPot = pots.reduce((s, p) => s + p.size, 0);

    const acting = t.isBettingRoundInProgress() ? t.playerToAct() : null;
    const round = t.roundOfBetting();

    const players = names.map((name, i) => {
      const inHand = hp[i];
      const st = seats[i];
      const folded = inHand === null && st !== null && holes[i];
      const src = inHand || st;
      return {
        name,
        chips: src ? src.stack : 0,
        bet: src ? src.betSize : 0,
        folded,
        hole: holes[i] || [],
      };
    });

    let message = '';
    if (acting !== null) {
      message =
        acting === 0
          ? `Ваш ход (${round}).`
          : `Ходит ${names[acting]}. Ожидайте…`;
    } else {
      message = `${round}: ожидание следующей улицы…`;
    }

    return {
      status: 'playing',
      dealer: t.button(),
      acting,
      board,
      message,
      players,
      pot: mainPot,
      lastEval: [],
      winnerSeats: [],
      winnerSummary: null,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- uiTick намеренно триггерит синхронизацию с Table
  }, [handOver, names, uiTick]);

  const heroChipsTotal = tableRef.current?.seats()?.[0]?.totalChips ?? 0;
  const heroBusted = view?.status === 'handOver' && heroChipsTotal === 0;
  /** После showdown busted-соперники снимаются — один игрок с фишками = стол выигран. */
  const clearedTableVictory =
    Boolean(view?.status === 'handOver') &&
    heroChipsTotal > 0 &&
    tableRef.current != null &&
    isHeroSoloAtTable(tableRef.current);

  const pokerOutcomeExportText = useMemo(() => {
    if (!handOver || !view) return '';
    const base = buildPokerPracticeOutcomeExport({
      handNo,
      names,
      handOutcomeStackDelta,
      winnerSummary: view?.winnerSummary,
      handOver,
      lastSeatActions,
      seatedSeats,
    });
    if (!clearedTableVictory) return base;
    return `=== Полная победа за столом ===\n${base}`;
  }, [
    handOver,
    handNo,
    names,
    handOutcomeStackDelta,
    view,
    lastSeatActions,
    seatedSeats,
    clearedTableVictory,
  ]);

  const pokerActionChainSteps = useMemo(
    () =>
      seatedSeats
        .map((seat) => {
          const a = lastSeatActions[seat];
          if (!a?.kind) return null;
          const nm = names[seat] || `М${seat}`;
          return `${nm}: ${formatPokerSeatActionForExport(a)}`;
        })
        .filter(Boolean),
    [seatedSeats, lastSeatActions, names]
  );

  const handleCopyPokerOutcome = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(pokerOutcomeExportText);
      setOutcomeCopyFeedback(true);
      if (outcomeCopyTimerRef.current) clearTimeout(outcomeCopyTimerRef.current);
      outcomeCopyTimerRef.current = setTimeout(() => {
        setOutcomeCopyFeedback(false);
        outcomeCopyTimerRef.current = null;
      }, 2200);
    } catch {
      /* ignore */
    }
  }, [pokerOutcomeExportText]);

  const heroEval = useMemo(() => {
    if (!view || view.status !== 'playing') return null;
    const hole = view.players[0].hole;
    const ecHole = hole.map(pokerTsCardToEngineCard).filter(Boolean);
    const ecBoard = view.board.map(pokerTsCardToEngineCard).filter(Boolean);
    if (ecHole.length !== 2 || ecBoard.length < 3) return null;
    return evaluateHand(ecHole, ecBoard);
  }, [view]);

  /** Диапазон легальных бет/рейзов для героя (если min < max — показываем слайдер). */
  const raiseRange = useMemo(() => {
    if (!view || view.status !== 'playing') return null;
    const t = tableRef.current;
    if (!t.isHandInProgress() || !t.isBettingRoundInProgress()) return null;
    if (t.playerToAct() !== 0 || view.players[0]?.folded) return null;
    const la = t.legalActions();
    const { actions: legal, chipRange } = la;
    if (!chipRange || (!legal.includes('raise') && !legal.includes('bet'))) return null;
    const min = chipRange.min;
    const max = chipRange.max;
    if (min >= max) return null;
    return { min, max, isBet: legal.includes('bet') };
  }, [view]);

  const clampedRaiseBet = useMemo(() => {
    if (!raiseRange) return null;
    const { min, max } = raiseRange;
    const raw = raiseAmount == null ? min : raiseAmount;
    return Math.min(max, Math.max(min, raw));
  }, [raiseRange, raiseAmount]);

  useEffect(() => {
    if (!raiseRange) return undefined;
    const { min, max } = raiseRange;
    setRaiseAmount((prev) => {
      const raw = prev == null ? min : prev;
      return Math.min(max, Math.max(min, raw));
    });
    return undefined;
  }, [raiseRange]);

  const runAfterMutation = useCallback(() => {
    const t = tableRef.current;
    const done = advanceUntilPauseOrDone(t);
    if (done) {
      play('showdown');
      setHandOver(done);
    }
    bump();
  }, [bump, play]);

  const dispatchHuman = useCallback(
    (action) => {
      const t = tableRef.current;
      if (!t.isHandInProgress() || !t.isBettingRoundInProgress()) return;
      if (t.playerToAct() !== 0) return;

      const street = t.roundOfBetting();
      const { actions, chipRange } = t.legalActions();

      if (action === 'FOLD' && actions.includes('fold')) {
        play('fold');
        t.actionTaken('fold');
        tagSeatAction(0, { street, kind: 'fold' });
        runAfterMutation();
        return;
      }
      if (action === 'CHECK' && actions.includes('check')) {
        play('check');
        t.actionTaken('check');
        tagSeatAction(0, { street, kind: 'check' });
        runAfterMutation();
        return;
      }
      if (action === 'CALL' && actions.includes('call')) {
        play('call');
        t.actionTaken('call');
        tagSeatAction(0, { street, kind: 'call' });
        runAfterMutation();
        return;
      }
      if (action === 'RAISE' && chipRange) {
        const lo = chipRange.min;
        const hi = chipRange.max;
        const betSize = Math.min(hi, Math.max(lo, raiseAmount ?? lo));
        if (actions.includes('raise')) {
          play('raise');
          t.actionTaken('raise', betSize);
          tagSeatAction(0, { street, kind: 'raise', amount: betSize });
        } else if (actions.includes('bet')) {
          play('raise');
          t.actionTaken('bet', betSize);
          tagSeatAction(0, { street, kind: 'bet', amount: betSize });
        }
        runAfterMutation();
      }
    },
    [play, runAfterMutation, tagSeatAction, raiseAmount]
  );

  /** Автоход по истечении времени: чек → колл → мин. бет/рейз → пас. */
  const autoHeroOnDeadline = useCallback(() => {
    const t = tableRef.current;
    if (!t.isHandInProgress() || !t.isBettingRoundInProgress()) return;
    if (t.playerToAct() !== 0) return;
    const street = t.roundOfBetting();
    const { actions: legal, chipRange } = t.legalActions();

    if (legal.includes('check')) {
      play('check');
      t.actionTaken('check');
      tagSeatAction(0, { street, kind: 'check' });
      runAfterMutation();
      return;
    }
    if (legal.includes('call')) {
      play('call');
      t.actionTaken('call');
      tagSeatAction(0, { street, kind: 'call' });
      runAfterMutation();
      return;
    }
    if (chipRange && (legal.includes('raise') || legal.includes('bet'))) {
      const lo = chipRange.min;
      const hi = chipRange.max;
      const betSize = Math.min(hi, Math.max(lo, lo));
      if (legal.includes('raise')) {
        play('raise');
        t.actionTaken('raise', betSize);
        tagSeatAction(0, { street, kind: 'raise', amount: betSize });
      } else {
        play('raise');
        t.actionTaken('bet', betSize);
        tagSeatAction(0, { street, kind: 'bet', amount: betSize });
      }
      runAfterMutation();
      return;
    }
    if (legal.includes('fold')) {
      play('fold');
      t.actionTaken('fold');
      tagSeatAction(0, { street, kind: 'fold' });
      runAfterMutation();
    }
  }, [play, runAfterMutation, tagSeatAction]);

  useEffect(() => {
    const t = tableRef.current;
    if (handOver) {
      setHeroTurnSecondsLeft(null);
      return undefined;
    }
    if (!t.isHandInProgress() || !t.isBettingRoundInProgress()) {
      setHeroTurnSecondsLeft(null);
      return undefined;
    }
    if (t.playerToAct() !== 0) {
      setHeroTurnSecondsLeft(null);
      return undefined;
    }
    if (!t.handPlayers()[0]) {
      setHeroTurnSecondsLeft(null);
      return undefined;
    }

    const deadline = Date.now() + HERO_TURN_SECONDS * 1000;
    setHeroTurnSecondsLeft(HERO_TURN_SECONDS);

    const tickId = setInterval(() => {
      const left = Math.ceil((deadline - Date.now()) / 1000);
      setHeroTurnSecondsLeft(Math.max(0, left));
    }, 200);

    const timeoutId = setTimeout(() => {
      clearInterval(tickId);
      setHeroTurnSecondsLeft(null);
      autoHeroOnDeadline();
    }, HERO_TURN_SECONDS * 1000);

    return () => {
      clearInterval(tickId);
      clearTimeout(timeoutId);
      setHeroTurnSecondsLeft(null);
    };
  }, [uiTick, handOver, autoHeroOnDeadline]);

  const heroFolded = view?.players[0]?.folded;

  useEffect(() => {
    const t = tableRef.current;
    if (handOver) {
      setOppTurnClock(null);
      return undefined;
    }
    if (!t.isHandInProgress() || !t.isBettingRoundInProgress()) {
      setOppTurnClock(null);
      return undefined;
    }
    const seat = t.playerToAct();
    if (seat === 0 || seat == null) {
      setOppTurnClock(null);
      return undefined;
    }
    const totalMs = heroFolded ? BOT_TURN_DELAY_HERO_FOLDED_MS : BOT_TURN_DELAY_MS;
    setOppTurnClock({ endAt: Date.now() + totalMs, totalMs });
    const iv = setInterval(() => setOppRingTick((x) => x + 1), 50);
    return () => {
      clearInterval(iv);
      setOppTurnClock(null);
    };
  }, [uiTick, handOver, heroFolded]);

  useEffect(() => {
    const t = tableRef.current;
    if (!t.isHandInProgress() || !t.isBettingRoundInProgress()) return undefined;
    if (t.playerToAct() === 0) return undefined;
    if (handOver) return undefined;

    const delay = heroFolded ? BOT_TURN_DELAY_HERO_FOLDED_MS : BOT_TURN_DELAY_MS;
    const id = setTimeout(() => {
      const acted = botPlay(t, play);
      if (acted) {
        tagSeatAction(acted.seat, {
          street: acted.street,
          kind: acted.kind,
          amount: acted.amount,
        });
      }
      const done = advanceUntilPauseOrDone(t);
      if (done) {
        play('showdown');
        setHandOver(done);
      }
      bump();
    }, delay);
    return () => clearTimeout(id);
  }, [uiTick, handOver, heroFolded, play, bump, tagSeatAction]);

  useEffect(() => {
    if (!handOver || outcomeKind == null) return undefined;

    const t = tableRef.current;
    const heroTotal = t.seats()[0]?.totalChips ?? 0;
    const delta = heroTotal - heroStackStart;

    if (outcomeKind === 'win') {
      play('win');
      setBalanceFx('gain');
    } else if (outcomeKind === 'lose') {
      play('lose');
      setBalanceFx('loss');
    } else {
      play('chip-stack');
      setBalanceFx(delta >= 0 ? 'gain' : 'loss');
    }
    setFloatingDelta(delta);
    const timer = setTimeout(() => {
      setBalanceFx(null);
      setFloatingDelta(null);
    }, 2800);
    return () => clearTimeout(timer);
  }, [handOver, outcomeKind, play, heroStackStart]);

  const startPracticeSession = useCallback(() => {
    const t = createTable(botCount + 1);
    t.startHand(0);
    tableRef.current = t;
    setGameStarted(true);
    setHandOver(null);
    setHandNo(0);
    setBalanceFx(null);
    setFloatingDelta(null);
    setRaiseAmount(null);
    setLastSeatActions([null, null, null, null]);
    setPokerOutcomeDetailsExpanded(false);
    setHeroStackStart(t.seats()[0]?.totalChips ?? START_STACK);
    bump();
  }, [botCount, bump]);

  /** Новый стол и стеки (герой без фишек / заново после полной победы). */
  const restartPracticeSession = useCallback(() => {
    const t = createTable(botCount + 1);
    t.startHand(0);
    tableRef.current = t;
    setGameStarted(true);
    setHandOver(null);
    setHandNo(0);
    setBalanceFx(null);
    setFloatingDelta(null);
    setRaiseAmount(null);
    setLastSeatActions([null, null, null, null]);
    setPokerOutcomeDetailsExpanded(false);
    setHeroStackStart(t.seats()[0]?.totalChips ?? START_STACK);
    bump();
  }, [botCount, bump]);

  const startNextHand = useCallback(() => {
    const t = tableRef.current;
    if (countSeatedPlayers(t) < 2) {
      restartPracticeSession();
      return;
    }
    setHandOver(null);
    setBalanceFx(null);
    setFloatingDelta(null);
    setRaiseAmount(null);
    setLastSeatActions([null, null, null, null]);
    setPokerOutcomeDetailsExpanded(false);
    const nextNo = handNo + 1;
    setHandNo(nextNo);
    const activeSeats = t
      .seats()
      .map((seat, idx) => (seat != null ? idx : null))
      .filter((idx) => idx != null);
    const dealerSeat = activeSeats.length > 0 ? activeSeats[nextNo % activeSeats.length] : 0;
    t.startHand(dealerSeat);
    setHeroStackStart(t.seats()[0]?.totalChips ?? START_STACK);
    bump();
  }, [handNo, bump, restartPracticeSession]);

  useEffect(() => {
    if (handOver) setPokerOutcomeDetailsExpanded(false);
  }, [handOver]);

  if (!gameStarted) {
    return (
      <div className="pt-root relative flex min-h-screen flex-col overflow-hidden bg-background text-foreground">
        <ArcadeModeBackdrop mode="poker" />
        <GameIndustrialHeader
          floating
          tableName="Покер · тренировка с ботами"
          blindsLabel="Настройка стола"
          playersCount={botCount + 1}
          maxPlayers={N}
          onLeave={onBack}
        />
        <main className="relative z-10 mx-auto flex w-full max-w-xl flex-1 flex-col gap-3 overflow-y-auto px-3 pb-4 pt-3 sm:gap-4 sm:px-4">
          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
            <div className="absolute -left-16 top-6 h-44 w-44 rounded-full bg-cyan-400/12 blur-3xl" />
            <div className="absolute right-[-3.5rem] top-24 h-40 w-40 rounded-full bg-indigo-400/16 blur-3xl" />
            <div className="absolute bottom-10 left-1/2 h-52 w-52 -translate-x-1/2 rounded-full bg-sky-400/8 blur-3xl" />
            <div className="absolute inset-x-6 top-2 h-px bg-gradient-to-r from-transparent via-cyan-300/30 to-transparent" />
          </div>

          <div className="relative overflow-hidden rounded-2xl border border-emerald-300/25 bg-gradient-to-br from-[#133525]/88 via-[#0f2a1f]/84 to-[#0a1e16]/88 px-3 py-2.5 shadow-[0_18px_46px_rgba(0,0,0,0.4)]">
            <div className="pointer-events-none absolute inset-0" aria-hidden>
              <div className="absolute left-[10%] top-[12%] h-20 w-20 rounded-full bg-emerald-300/18 blur-2xl" />
              <div className="absolute right-[12%] bottom-[10%] h-16 w-16 rounded-full bg-teal-300/14 blur-2xl" />
            </div>
            <img
              src="/logotip-poker-button.png"
              alt=""
              className="relative z-[1] mx-auto block h-auto max-h-[78px] w-full max-w-[min(320px,92%)] origin-center object-contain object-center transition-transform duration-200 ease-out sm:max-h-[88px]"
              aria-hidden="true"
              draggable={false}
            />
          </div>
          <section className="overflow-visible bg-transparent shadow-none">
            <div className="flex flex-col items-center px-0 py-1 text-center">
              <div className="mb-2 flex shrink-0 items-center justify-center bg-transparent shadow-none">
                <Swords className="h-24 w-24 text-emerald-200" />
              </div>
              <p className="text-base font-black text-emerald-100 sm:text-[1.05rem]">Практика покера против ботов</p>
              <p className="mt-1 max-w-[34rem] text-xs leading-relaxed text-slate-300/90 sm:text-[13px]">
                Выберите состав стола и начните сессию. Игровой HUD и действия откроются после старта.
              </p>
            </div>
          </section>

          <section className="rounded-2xl border border-white/10 bg-gradient-to-b from-[#111a2a]/92 to-[#0b1320]/92 p-3.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] sm:p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <p className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">Параметры стола</p>
              <span className="rounded-full border border-emerald-300/30 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-100">
                {botCount + 1}/{N} игроков
              </span>
            </div>

            <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Количество ботов
            </label>
            <div className="grid grid-cols-3 gap-2">
              {BOT_COUNT_OPTIONS.map((count) => (
                <button
                  key={count}
                  type="button"
                  onClick={() => setBotCount(count)}
                  aria-label={`${count} бота(ов)`}
                  className={cn(
                    'group overflow-hidden rounded-xl border px-2 py-2.5 text-sm font-bold transition min-h-[62px]',
                    botCount === count
                      ? 'border-emerald-300/65 bg-emerald-400/15 text-emerald-100 shadow-[0_0_0_1px_rgba(110,231,183,0.22)]'
                      : 'border-white/10 bg-[#0a1626]/70 text-slate-200 hover:border-emerald-300/35 hover:bg-emerald-500/10'
                  )}
                >
                  <span className="inline-flex items-center justify-center gap-1 sm:gap-1.5">
                    {Array.from({ length: count }).map((_, idx) => (
                      <Bot
                        key={`${count}-bot-icon-${idx}`}
                        strokeWidth={2.35}
                        className={cn(
                          count === 1 ? 'h-9 w-9 sm:h-10 sm:w-10' : count === 2 ? 'h-7 w-7 sm:h-8 sm:w-8' : 'h-6 w-6 sm:h-7 sm:w-7',
                          'drop-shadow-[0_0_10px_rgba(16,185,129,0.28)]',
                          botCount === count ? 'text-emerald-200' : 'text-slate-400 group-hover:text-emerald-200'
                        )}
                      />
                    ))}
                  </span>
                </button>
              ))}
            </div>

            <p className="mt-2.5 text-[11px] text-slate-400">
              Матчап: <span className="font-semibold text-emerald-100">{user?.username || 'Вы'}</span> vs {botCount} бота(ов)
            </p>
          </section>

          <button
            type="button"
            onClick={startPracticeSession}
            className="mx-auto mt-0.5 flex w-full max-w-md items-center justify-center rounded-2xl border border-emerald-300/45 bg-gradient-to-b from-emerald-500/25 to-emerald-700/20 py-3 text-sm font-black text-emerald-100 shadow-[0_12px_28px_rgba(16,185,129,0.2)] transition hover:border-emerald-200/65 hover:from-emerald-400/30 hover:to-emerald-700/28"
          >
            НАЧАТЬ
          </button>
        </main>
      </div>
    );
  }

  if (!view) {
    return null;
  }

  const heroUiCards = (view.players[0].hole || []).map(pokerTsCardToPlayingCard).filter(Boolean);
  const communitySlots = boardToCommunitySlots(
    (view.board || []).map(pokerTsCardToEngineCard).filter(Boolean)
  );

  const showOppCards = view.status === 'handOver';

  const tLive = tableRef.current;
  const canAct =
    view.status === 'playing' &&
    tLive.isHandInProgress() &&
    tLive.isBettingRoundInProgress() &&
    tLive.playerToAct() === 0 &&
    !view.players[0].folded;

  const actions = [];
  if (canAct) {
    const la = tLive.legalActions();
    const { actions: legal, chipRange } = la;
    if (legal.includes('fold')) actions.push({ action: 'FOLD', label: 'Пас' });
    if (legal.includes('call')) {
      const amt = callAmount(tLive);
      actions.push({ action: 'CALL', label: amt > 0 ? `Уравнять ${amt}` : 'Уравнять' });
    }
    if (legal.includes('check')) actions.push({ action: 'CHECK', label: 'Чек' });
    if ((legal.includes('raise') || legal.includes('bet')) && chipRange) {
      const lo = chipRange.min;
      const hi = chipRange.max;
      const chipBet = raiseRange && clampedRaiseBet != null ? clampedRaiseBet : lo;
      const suffix = hi > lo && chipBet >= hi ? ' · олл-ин' : '';
      const amtLabel = chipBet.toLocaleString('ru-RU');
      actions.push({
        action: 'RAISE',
        label: legal.includes('bet')
          ? `Бет ${amtLabel}${suffix}`
          : lo === hi
            ? `Рейз ${amtLabel}${suffix}`
            : `Рейз ${amtLabel}${suffix}`,
      });
    }
  }

  const seatDensity = getSeatDensityPreset(seatedSeats.length);
  const seatArena = getSeatArenaTuning(seatedSeats.length);
  const seatSlots = buildEllipticSeatSlots({
    seats: seatedSeats,
    heroSeat: 0,
    centerY: seatArena.centerY,
    radiusX: seatArena.radiusX,
    radiusY: seatArena.radiusY,
  });

  const heroStripName =
    view.status === 'handOver'
      ? (() => {
          const ecHole = (view.players[0].hole || []).map(pokerTsCardToEngineCard).filter(Boolean);
          const ecBoard = (view.board || []).map(pokerTsCardToEngineCard).filter(Boolean);
          if (ecHole.length === 2 && ecBoard.length >= 3) {
            return evaluateHand(ecHole, ecBoard).name;
          }
          return undefined;
        })()
      : heroEval?.name;

  return (
    <div className="pt-root relative flex min-h-screen flex-col overflow-hidden bg-background text-foreground">
      <ArcadeModeBackdrop mode="poker" />
      {view.status === 'handOver' && clearedTableVictory && (
        <div
          className="fixed inset-0 z-[60] overflow-y-auto overscroll-contain bg-black/40"
          aria-live="assertive"
        >
          <div className="poker-fx-modal-overlay-inner flex min-h-[100dvh] w-full items-center justify-center p-3 py-3 sm:p-4">
            <div className="pointer-events-auto poker-fx-modal-shell poker-fx-grand-victory-panel relative w-full max-w-[min(94vw,560px)] min-h-0 max-h-[min(85dvh,calc(100dvh-1.5rem))] overflow-y-auto rounded-2xl border border-amber-400/50 bg-gradient-to-b from-amber-950/95 via-[#0f1428]/98 to-[#0a1520]/98 px-6 py-6 text-center shadow-[0_0_80px_rgba(251,191,36,0.35)] sm:px-8 sm:py-8">
            <div className="poker-fx-fireworks rounded-2xl" aria-hidden>
              {GRAND_VICTORY_SPARKS.map((s, i) => (
                <span
                  key={i}
                  className="poker-fx-firework-dot"
                  style={{
                    left: `${s.l}%`,
                    top: `${s.t}%`,
                    animationDelay: `${s.d}s`,
                    color: s.c,
                  }}
                />
              ))}
            </div>
            <div className="relative z-10">
              <Sparkles className="mx-auto mb-3 h-14 w-14 text-amber-300 drop-shadow-[0_0_18px_rgba(251,191,36,0.75)]" aria-hidden />
              <p className="text-xl font-black uppercase tracking-wide text-white drop-shadow-sm">
                Вы одолели всех игроков!
              </p>
              <p className="mt-2 text-base font-bold text-amber-100/95">Поздравляем!</p>
              {handOutcomeStackDelta != null && (
                <p
                  className={cn(
                    'mt-2 font-mono text-lg font-black tabular-nums tracking-tight text-amber-100',
                    handOutcomeStackDelta > 0 && 'drop-shadow-[0_0_12px_rgba(251,191,36,0.35)]'
                  )}
                >
                  {handOutcomeStackDelta > 0 ? '+' : ''}
                  {handOutcomeStackDelta.toLocaleString('ru-RU')} SEKA
                </p>
              )}
              {view.winnerSummary && (
                <p className="mt-4 text-sm font-semibold leading-snug text-amber-50/90">{view.winnerSummary}</p>
              )}
              <button
                type="button"
                onClick={handleCopyPokerOutcome}
                className="relative z-10 mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-amber-400/50 bg-amber-950/45 py-2.5 text-xs font-bold text-amber-100 transition hover:bg-amber-900/55"
              >
                <Copy className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {outcomeCopyFeedback ? 'Снимок скопирован' : 'Копировать снимок раздачи'}
              </button>
              <div className="relative z-10 mt-6 flex w-full flex-col gap-2">
                <button
                  type="button"
                  onClick={restartPracticeSession}
                  className="flex w-full items-center justify-center rounded-xl border border-amber-400/55 bg-amber-950/55 py-3 text-sm font-bold text-amber-100 shadow-[0_0_24px_rgba(251,191,36,0.22)] transition hover:bg-amber-900/55"
                >
                  Играть заново
                </button>
                <button
                  type="button"
                  onClick={onBack}
                  className="flex w-full items-center justify-center rounded-xl border border-red-500/45 bg-red-950/50 py-3 text-sm font-bold text-red-200 transition hover:bg-red-900/55"
                >
                  Выйти из игры
                </button>
              </div>
            </div>
          </div>
          </div>
        </div>
      )}

      {view.status === 'handOver' && outcomeKind && !clearedTableVictory && (
        <div
          className="fixed inset-0 z-[60] overflow-y-auto overscroll-contain bg-black/25"
          aria-live="polite"
        >
          <div className="poker-fx-modal-overlay-inner flex min-h-[100dvh] w-full items-center justify-center p-3 py-3 sm:p-4">
          <div
            className={cn(
              'pointer-events-auto poker-fx-modal-shell w-full max-w-[min(94vw,560px)] min-h-0 max-h-[min(85dvh,calc(100dvh-1.5rem))] overflow-y-auto rounded-2xl px-4 py-4 text-center sm:px-8 sm:py-7',
              outcomeKind === 'win' &&
                'poker-fx-win-panel border border-emerald-400/50 bg-gradient-to-b from-emerald-950/95 to-[#0a1520]/95 shadow-[0_0_60px_rgba(52,211,153,0.25)]',
              outcomeKind === 'lose' &&
                'poker-fx-loss-panel border border-red-500/45 bg-gradient-to-b from-red-950/90 to-[#0a1520]/95 shadow-[0_0_40px_rgba(248,113,113,0.2)]',
              outcomeKind === 'chop' &&
                'border border-amber-400/45 bg-gradient-to-b from-amber-950/90 to-[#0a1520]/95 shadow-lg'
            )}
          >
            {outcomeKind === 'win' && (
              <>
                <Trophy className="mx-auto mb-2 h-12 w-12 text-emerald-300 drop-shadow-[0_0_12px_rgba(52,211,153,0.6)]" aria-hidden />
                <p className="text-lg font-black tracking-wide text-white">Победа</p>
                {handOutcomeStackDelta != null && (
                  <p
                    className={cn(
                      'mt-1 font-mono text-xl font-black tabular-nums tracking-tight',
                      handOutcomeStackDelta > 0 &&
                        'text-emerald-300 drop-shadow-[0_0_12px_rgba(52,211,153,0.35)]',
                      handOutcomeStackDelta <= 0 && 'text-emerald-200/90'
                    )}
                  >
                    {handOutcomeStackDelta > 0 ? '+' : ''}
                    {handOutcomeStackDelta.toLocaleString('ru-RU')} SEKA
                  </p>
                )}
                <p className="mt-2 text-sm text-emerald-100/90">Банк ваш — отличная игра.</p>
                {view.winnerSummary && (
                  <p className="mt-3 text-base font-bold leading-snug text-emerald-200/95">{view.winnerSummary}</p>
                )}
              </>
            )}
            {outcomeKind === 'lose' && (
              <>
                <Skull className="mx-auto mb-2 h-11 w-11 text-red-300/90" aria-hidden />
                <p className="text-lg font-black tracking-wide text-white">Раздача проиграна</p>
                {handOutcomeStackDelta != null && (
                  <p
                    className={cn(
                      'mt-1 font-mono text-xl font-black tabular-nums tracking-tight',
                      handOutcomeStackDelta < 0 &&
                        'text-red-300 drop-shadow-[0_0_12px_rgba(248,113,113,0.28)]',
                      handOutcomeStackDelta >= 0 && 'text-red-200/90'
                    )}
                  >
                    {handOutcomeStackDelta > 0 ? '+' : ''}
                    {handOutcomeStackDelta.toLocaleString('ru-RU')} SEKA
                  </p>
                )}
                <p className="mt-2 text-sm text-red-100/80">Попробуйте ещё раз в следующей.</p>
                {view.winnerSummary && (
                  <p className="mt-3 text-base font-bold leading-snug text-white">{view.winnerSummary}</p>
                )}
              </>
            )}
            {outcomeKind === 'chop' && (
              <>
                <p className="text-lg font-black tracking-wide text-amber-100">Ничья</p>
                {handOutcomeStackDelta != null && (
                  <p
                    className={cn(
                      'mt-1 font-mono text-xl font-black tabular-nums tracking-tight',
                      handOutcomeStackDelta > 0 && 'text-emerald-300/95',
                      handOutcomeStackDelta < 0 && 'text-red-300/95',
                      handOutcomeStackDelta === 0 && 'text-amber-200/95'
                    )}
                  >
                    {handOutcomeStackDelta > 0 ? '+' : ''}
                    {handOutcomeStackDelta.toLocaleString('ru-RU')} SEKA
                  </p>
                )}
                <p className="mt-2 text-sm text-amber-100/80">Банк поделён между лучшими руками.</p>
                {view.winnerSummary && (
                  <p className="mt-3 text-base font-bold leading-snug text-amber-100">{view.winnerSummary}</p>
                )}
              </>
            )}
            <div className="mt-4 flex w-full flex-wrap items-center justify-between gap-2">
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 bg-black/30 px-2.5 py-1.5 text-[10px] font-bold text-amber-100 transition hover:bg-black/45"
              >
                <ArrowLeft className="h-3.5 w-3.5 shrink-0" aria-hidden />
                Назад
              </button>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={handleCopyPokerOutcome}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[10px] font-bold transition',
                    outcomeCopyFeedback
                      ? 'border-emerald-400/60 bg-emerald-500/20 text-emerald-100'
                      : outcomeKind === 'win'
                        ? 'border-emerald-400/45 bg-emerald-500/12 text-emerald-100 hover:bg-emerald-500/22'
                        : outcomeKind === 'lose'
                          ? 'border-red-400/45 bg-red-500/12 text-red-100 hover:bg-red-500/22'
                          : 'border-amber-400/45 bg-amber-500/12 text-amber-100 hover:bg-amber-500/22'
                  )}
                >
                  <Copy className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  {outcomeCopyFeedback ? 'Снимок скопирован' : 'Копировать снимок раздачи'}
                </button>
              </div>
            </div>
            {(Boolean(handOver) || pokerActionChainSteps.length > 0) && (
              <button
                type="button"
                onClick={() => setPokerOutcomeDetailsExpanded((v) => !v)}
                aria-expanded={pokerOutcomeDetailsExpanded}
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 bg-black/25 px-3 py-2 text-[11px] font-bold text-white/90 transition hover:bg-black/35"
              >
                {pokerOutcomeDetailsExpanded ? (
                  <>
                    <ChevronUp className="h-4 w-4 shrink-0" aria-hidden />
                    Скрыть детали и историю
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                    Показать детали и историю
                  </>
                )}
              </button>
            )}
            {pokerOutcomeDetailsExpanded ? (
              <>
            {handOver ? (
              <div className="mt-3 w-full rounded-lg border border-cyan-500/30 bg-black/25 px-2 py-2 text-left">
                <p className="text-center text-[10px] font-semibold uppercase tracking-wide text-cyan-100/90">
                  Карты и вскрытие
                </p>
                {handOver.boardSnap?.length ? (
                  <div className="mt-2">
                    <p className="text-[9px] font-semibold text-cyan-200/75">Борд</p>
                    <div className="mt-1 flex flex-wrap justify-center gap-0.5">
                      {handOver.boardSnap.map((c, i) => {
                        const pc = pokerTsCardToPlayingCard(c);
                        return pc ? (
                          <PlayingCard key={`out-b-${i}`} suit={pc.suit} rank={pc.rank} size="sm" />
                        ) : null;
                      })}
                    </div>
                  </div>
                ) : null}
                <div className="mt-2 space-y-2">
                  {seatedSeats.map((seat) => {
                    const holes = handOver.holesSnap?.[seat];
                    if (!holes || holes.length === 0) return null;
                    const nm = names[seat] || `М${seat}`;
                    return (
                      <div key={`out-hole-${seat}`} className="rounded-md border border-white/10 bg-black/30 px-2 py-1.5">
                        <p className="text-[9px] font-semibold text-white/85">{nm}</p>
                        <div className="mt-1 flex flex-wrap justify-center gap-0.5">
                          {holes.map((c, i) => {
                            const pc = pokerTsCardToPlayingCard(c);
                            return pc ? (
                              <PlayingCard key={`out-h-${seat}-${i}`} suit={pc.suit} rank={pc.rank} size="sm" />
                            ) : null;
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {handOver.lastEval?.length ? (
                  <div className="mt-2 border-t border-white/10 pt-2">
                    <p className="text-[9px] font-semibold text-neon-gold/95">Комбинации</p>
                    <ul className="mt-1 space-y-0.5 text-[10px] text-amber-100/90">
                      {handOver.lastEval.map((row, i) => (
                        <li key={`ev-${row.seat}-${i}`}>
                          {names[row.seat] ?? row.seat}: {row.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : null}
            {pokerActionChainSteps.length > 0 ? (
              <div className="mt-3 max-h-[min(28vh,220px)] w-full overflow-y-auto rounded-lg border border-white/10 bg-black/20 px-2 py-2 text-left">
                <p className="text-center text-[10px] font-semibold uppercase tracking-wide text-amber-100/90">
                  Метки последних ходов
                </p>
                <p className="mt-0.5 text-center text-[9px] text-white/50">
                  По местам за раздачу
                </p>
                <MatchChronicleChain steps={pokerActionChainSteps} />
              </div>
            ) : null}
              </>
            ) : null}
            <div className="mt-6 flex w-full flex-col gap-2">
              <button
                type="button"
                onClick={heroBusted ? restartPracticeSession : startNextHand}
                className={
                  heroBusted
                    ? 'flex w-full items-center justify-center rounded-xl border border-amber-400/55 bg-amber-950/55 py-3 text-sm font-bold text-amber-100 shadow-[0_0_24px_rgba(251,191,36,0.18)] transition hover:bg-amber-900/55'
                    : 'flex w-full items-center justify-center rounded-xl border border-emerald-500/40 bg-emerald-950/40 py-3 text-sm font-bold text-emerald-200 transition hover:bg-emerald-900/50'
                }
              >
                {heroBusted ? 'Начать игру заново' : 'Следующая раздача'}
              </button>
              <button
                type="button"
                onClick={onBack}
                className="flex w-full items-center justify-center rounded-xl border border-red-500/45 bg-red-950/50 py-3 text-sm font-bold text-red-200 transition hover:bg-red-900/55"
              >
                Выйти из игры
              </button>
            </div>
          </div>
          </div>
        </div>
      )}

      <GameIndustrialHeader
        floating
        tableName={`Покер · ${seatedSeats.length}-max (poker-ts)`}
        blindsLabel={`${SB}/${BB} SEKA`}
        playersCount={seatedSeats.length}
        maxPlayers={N}
        onLeave={onBack}
      />

      <main className="relative z-10 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-3 py-3">
        {view.message && (
          <p className="text-center text-xs text-muted-foreground">{view.message}</p>
        )}

        {view.status === 'handOver' && view.winnerSummary && (
          <div
            className="rounded-xl border border-neon-gold/35 bg-gradient-to-b from-[#1a2418]/90 to-card/80 px-3 py-2.5 text-center shadow-inner ring-1 ring-neon-gold/15"
            role="status"
            aria-live="polite"
          >
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Итог раздачи</p>
            <p className="mt-1 text-sm font-bold leading-snug text-neon-gold sm:text-base">{view.winnerSummary}</p>
          </div>
        )}

        <div className={cn('relative mx-auto w-full flex-1', seatArena.maxWidthClass, seatArena.minHeightClass)}>
          <div className="absolute inset-0 flex items-center justify-center py-0.5">
            <PokerTableSurface communitySlots={communitySlots} pot={view.pot} potLabel="POT" />
          </div>
          <div className="pointer-events-none absolute inset-0 z-10">
            {seatSlots.map((slot) => {
              const seat = slot.seat;
              const pl = view.players[seat];
              if (!pl) return null;
              const uiCards = (pl.hole || []).map(pokerTsCardToPlayingCard).filter(Boolean);
              const nm = pl.name.length > 10 ? `${pl.name.slice(0, 9)}…` : pl.name;
              const won = view.winnerSeats?.includes(seat);
              const stackZero =
                pl.chips != null && Number.isFinite(Number(pl.chips)) && Number(pl.chips) <= 0;
              return (
                <div
                  key={seat}
                  className="absolute"
                  style={{
                    left: `${slot.leftPct}%`,
                    top: `${slot.topPct}%`,
                    transform: `translate(-50%, -50%) scale(${seatDensity.seatScale})`,
                    transformOrigin: 'center',
                  }}
                >
                  <div
                    className={cn(
                      'pointer-events-auto flex flex-col items-center gap-0.5',
                      seat === 0 && '-translate-x-1.5 sm:-translate-x-2'
                    )}
                  >
                    <IndustrialSeat
                      name={nm}
                      chips={pl.chips}
                      chipsDisplay={seatDensity.hideSecondaryMeta ? undefined : pl.chips}
                      bet={pl.bet}
                      cards={seat === 0 ? [] : uiCards.length ? uiCards : []}
                      compact={seatDensity.compact}
                      seatColorIndex={seat}
                      isDealer={view.dealer === seat}
                      isCurrentTurn={view.acting === seat}
                      isFolded={pl.folded}
                      isWinner={won}
                      showCards={seat === 0 || showOppCards}
                      isUser={seat === 0}
                      turnTimeLeftSec={seat === 0 ? heroTurnSecondsLeft : null}
                      turnTimeTotalSec={seat === 0 ? HERO_TURN_SECONDS : null}
                      turnTimeFraction={
                        view.acting != null &&
                        view.acting !== 0 &&
                        seat === view.acting &&
                        oppTurnClock
                          ? Math.max(
                              0,
                              Math.min(1, (oppTurnClock.endAt - Date.now()) / oppTurnClock.totalMs)
                            )
                          : undefined
                      }
                      showDefeatCross={
                        seat === 0 &&
                        view.status === 'handOver' &&
                        outcomeKind === 'lose' &&
                        !clearedTableVictory
                      }
                      showEliminatedCross={stackZero}
                    />
                    {!seatDensity.hideLastActionBadges ? (
                      <SeatLastMoveBadge
                        key={lastSeatActions[seat]?.at ?? `idle-${seat}`}
                        action={lastSeatActions[seat]}
                        isDimmed={pl.folded}
                      />
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {view.lastEval && view.lastEval.length > 0 && (
          <div className="rounded-lg border border-border/60 bg-card/60 px-3 py-2 text-center text-[10px] leading-relaxed text-muted-foreground sm:text-[11px]">
            {view.lastEval.map((row, idx) => (
              <span key={`${row.seat}-${idx}`} className="inline-block px-1">
                <span className="text-neon-gold">{view.players[row.seat]?.name ?? row.seat}:</span> {row.name}
              </span>
            ))}
          </div>
        )}

        <PlayerHandStrip
          cards={heroUiCards}
          handName={heroStripName}
          faceDown={false}
        />

        <div className="sticky bottom-0 z-30 bg-gradient-to-t from-background via-background to-transparent pb-3 pt-1.5">
          {canAct && actions.length > 0 ? (
            <>
              {raiseRange != null && clampedRaiseBet != null && (
                <PokerBetRaiseSlider
                  min={raiseRange.min}
                  max={raiseRange.max}
                  value={clampedRaiseBet}
                  onChange={setRaiseAmount}
                  className="pb-2"
                />
              )}
              <SekaActionBar actions={actions} onAction={dispatchHuman} disabled={false} />
            </>
          ) : null}
          <div className={cn('mx-auto w-full max-w-[320px] sm:max-w-[360px]', canAct && actions.length > 0 && 'mt-2')}>
            <div className="relative">
              {floatingDelta != null && floatingDelta !== 0 && (
                <span
                  className={cn(
                    'pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 whitespace-nowrap rounded-full px-3 py-1 text-sm font-black tabular-nums poker-fx-chip-delta',
                    floatingDelta > 0
                      ? 'bg-emerald-500/20 text-emerald-200 ring-1 ring-emerald-400/40'
                      : 'bg-red-500/20 text-red-200 ring-1 ring-red-400/40'
                  )}
                >
                  {floatingDelta > 0 ? '+' : ''}
                  {floatingDelta.toLocaleString('ru-RU')} SEKA
                </span>
              )}
              <PlayerStackBar
                stackAmount={tableRef.current.seats()[0]?.totalChips ?? 0}
                balanceFx={balanceFx}
                className="px-3 py-1.5"
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function PokerHuPractice({ user, onBack }) {
  return (
    <AudioProvider>
      <PokerHuPracticeInner user={user} onBack={onBack} />
    </AudioProvider>
  );
}

export default PokerHuPractice;
